import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { PageHeader, Card, Pill } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import ReportPageWrap from "@/components/reports/ReportPageWrap";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";
import { printSupplierComplianceReport } from "@/lib/miscReportPrint";

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function SupplierComplianceReport() {
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();
  const [daysAhead, setDaysAhead] = useState(60);
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["supplier-compliance", branchId, daysAhead],
    queryFn: async () => {
      const { data: res } = await api.get("/franchise/supplier-compliance", {
        params: { ...reportParams, days_ahead: daysAhead },
      });
      return res;
    },
    enabled: Boolean(canQuery),
  });

  const { data: priceData, isLoading: pricesLoading } = useQuery({
    queryKey: ["ingredient-supplier-prices", branchId, fromDate, toDate],
    queryFn: async () => {
      const { data: res } = await api.get("/franchise/ingredient-supplier-prices", {
        params: { ...reportParams, from_date: fromDate, to_date: toDate, limit: 100 },
      });
      return res.rows;
    },
    enabled: Boolean(canQuery && fromDate && toDate),
  });

  return (
    <ReportPageWrap>
      <PageHeader
        title="Supplier Compliance & Ingredient Prices"
        subtitle="TRN, trade licence, halal certificate alerts and GRN price history"
        actions={
          <ReportExportButtons
            disabled={!data && !priceData}
            title="Supplier Compliance & Ingredient Prices"
            filename="supplier-compliance"
            period={fromDate && toDate ? `Period: ${fromDate} to ${toDate}` : ""}
            onPrint={() =>
              printSupplierComplianceReport(
                { compliance: data, prices: priceData, from_date: fromDate, to_date: toDate },
                { branchId, allBranches },
              )
            }
          />
        }
      />

      <Card className="p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label>From</Label>
          <Input type="date" className="w-[160px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <Label>To</Label>
          <Input type="date" className="w-[160px]" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div>
          <Label>Alert window (days)</Label>
          <Input
            type="number"
            className="w-28"
            value={daysAhead}
            onChange={(e) => setDaysAhead(Number(e.target.value) || 60)}
          />
        </div>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          Refresh
        </Button>
        {data && (
          <Pill tone={data.alert_count ? "amber" : "green"}>
            {data.alert_count} supplier(s) need attention
          </Pill>
        )}
      </Card>

      <Tabs defaultValue="compliance">
        <TabsList className="mb-4">
          <TabsTrigger value="compliance">Compliance alerts</TabsTrigger>
          <TabsTrigger value="prices">Ingredient price history</TabsTrigger>
        </TabsList>

        <TabsContent value="compliance">
          {isLoading ? (
            <div className="flex gap-2 text-muted-foreground py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-2">
              {(data?.rows ?? []).map((row) => (
                <Card key={row.supplier_id} className="p-3 text-sm">
                  <div className="font-semibold flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    {row.supplier_name} ({row.supplier_code})
                  </div>
                  <ul className="mt-2 space-y-1 text-muted-foreground">
                    {row.issues.map((issue) => (
                      <li key={issue.type + issue.label}>
                        {issue.label}
                        {issue.date ? ` — ${issue.date}` : ""}
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
              {data?.alert_count === 0 && (
                <p className="text-muted-foreground py-6">All suppliers look compliant for the selected window.</p>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="prices">
          {pricesLoading ? (
            <div className="flex gap-2 text-muted-foreground py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <ReportTableCard title="Ingredient price history">
              <table>
                <thead>
                  <tr>
                    <th className="text-left">Date</th>
                    <th className="text-left">Ingredient</th>
                    <th className="text-left">Supplier</th>
                    <th className="text-right">Unit cost</th>
                  </tr>
                </thead>
                <tbody>
                  {(priceData ?? []).map((row) => (
                    <tr key={row.id}>
                      <td>{row.recorded_at?.slice(0, 10)}</td>
                      <td>{row.ingredient_name}</td>
                      <td>{row.supplier_name || "—"}</td>
                      <td className="text-right font-mono">{Number(row.unit_cost).toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ReportTableCard>
          )}
        </TabsContent>
      </Tabs>
    </ReportPageWrap>
  );
}
